/**
 * Credit card number pattern
 * Matches credit card numbers in the format XXXX-XXXX-XXXX-XXXX, with optional spaces or dashes.
 */ const creditCardPattern = /\b\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}\b/g;
/**
 * Phone number pattern
 * Matches phone numbers in the format XXX-XXX-XXXX or (XXX) XXX-XXXX, with optional dashes, periods, or parentheses.
 */ const phoneNumberPattern = /\b(?:\d{3}[-.]|\(\d{3}\)\s?)\d{3}[-.]?\d{4}\b/g;
/**
 * Mobile phone number pattern
 * Matches mobile phone numbers in the format +X (XXX) XXX-XXXX, with optional spaces.
 */ const mobilePhonePattern = /\b\+\d\s?\(\d{3}\)\s?\d{3}[-.]?\d{4}\b/g;
/**
 * SSN pattern
 * Matches social security numbers in the format XXX-XX-XXXX, with optional dashes or periods.
 */ const ssnPattern = /\b\d{3}[-.]?\d{2}[-.]?\d{4}\b/g;
/**
 * Federal Tax ID pattern
 * Matches federal tax IDs in the format XX-XXXXXXX, with optional dash.
 */ const taxIdPattern = /\b\d{2}-?\d{7}\b/g;
/**
 * Email pattern
 * Matches email addresses in the format `username@domain.tld`.
 */ const emailPattern = /\b[\w.-]+(?:\+[\w.-]+)?@[\w.-]+\.\w{2,}\b/gi;
/**
 * Helper to determine if string contains nested sensitive key, and return
 * string with redacted value.
 *
 * Checks for the following patterns:
 * - "key: value"
 * - "'key': 'value'"
 * - '"key: value"'
 * - "\"key:\": \"value\"".
 */ const redactKeyValue = (str, key)=>{
    const pattern = new RegExp(`(${key}:\\s*([^']*)|'${key}':\\s*'([^']*)'|"${key}":\\s*"([^"]*)"|\\\\\\"${key}\\\\\\":\\\\s*\\\\\\"([^\\\\\\"]*)\\\\\\")`, 'g');
    return str.replace(pattern, (match, _, value1, value2, value3, value4)=>{
        const value = value1 ?? value2 ?? value3 ?? value4;
        return match.replace(value, '***REDACTED***');
    });
};
/**
 * Redacts sensitive information in a given string.
 * Sensitive information includes:
 * - Credit card numbers
 * - Phone numbers (including mobile phone numbers)
 * - Social Security Numbers (SSNs)
 * - Federal Tax IDs
 * - IP addresses
 * - Email addresses.
 * @param value - The string to redact sensitive information from.
 * @returns The redacted string.
 */ const redactSensitiveStringsInValue = (value, keysToRedact = [])=>{
    // only redact string values
    if (typeof value !== 'string') return value;
    const stringKeys = keysToRedact.filter((key)=>typeof key === 'string');
    const regexpKeys = keysToRedact.filter((key)=>key instanceof RegExp);
    // Redact the complete value if one of the keys is present in that string
    if (keysToRedact.length > 0 && new RegExp(stringKeys.join('|'), 'gmi').test(value)) {
        for (const key of stringKeys){
            value = redactKeyValue(value, key);
        }
    }
    // Redact the complete value if one of the regexp is matching that string
    if (regexpKeys.length > 0) {
        for (const key of regexpKeys){
            if (key.test(value)) return '***REDACTED**';
        }
    }
    // Compile the patterns
    const patterns = [
        emailPattern,
        creditCardPattern,
        phoneNumberPattern,
        mobilePhonePattern,
        ssnPattern,
        taxIdPattern
    ];
    // Redact the sensitive information
    for (const pattern of patterns){
        value = value.toString().replace(pattern, '***REDACTED***');
    }
    return value;
};

export { redactSensitiveStringsInValue };
//# sourceMappingURL=redactSensitiveStringsInValue.js.map
